import { ScrollListProps, ScrollLoadList } from "@/components/ScrollLoadList";
import { useDevice } from "@/provider/DecideProvider";
import { SelfDropdown } from "@/ui/Dropdown";
import { Delete } from "@/ui/icons/Delete";
import { Edit } from "@/ui/icons/Edit";
import { MoreHorizontal } from "@/ui/icons/MoreHorizontal";
import { Pin } from "@/ui/icons/Pin";
import { UnPin } from "@/ui/icons/UnPin";
import { Panel, PanelProps } from "@/ui/Panel/Panel";
import { Avatar, Button, cn, DropdownItem, DropdownMenu, DropdownTrigger, Listbox, ListboxItem, ListboxProps } from "@heroui/react";
import { useTranslations } from "next-intl";
import { createContext, memo, ReactNode, useContext, useState } from "react";
import { createPortal } from "react-dom";

const LockContext = createContext<{ isLock: boolean, setLock: (b: boolean) => void }>(null!)



export const ChatListBox = ({ list,
  avatar,
  onPinned,
  onDelete,
  onSetName,
  onSelectionChange,
  ...props }: Omit<ListboxProps & ScrollListProps, 'children'> &
  {
    list: any[], avatar?: (item: any) => ReactNode,
    onPinned, onDelete, onSetName?
  }) => {
  const [isLock, setLock] = useState(false)
  const device = useDevice()


  return (
    <>
      <LockContext.Provider value={{ isLock, setLock }}>
        <ScrollLoadList
          aria-label="list"
          as={Listbox}
          classNames={{
            base: 'px-1'
          }}
          items={list}
          selectionMode="single"
          disallowEmptySelection
          hiddenLoading
          onSelectionChange={
            (val) => {
              onSelectionChange && onSelectionChange(val)
            }
          }
          {...props}
        >
          {list.map((item: any) => (
            <ListboxItem
              hideSelectedIcon
              style={{
                '--heroui-default': device === 'mobile' ? 'var(--lb-item-default-mobile)' : 'var(--lb-item-default)'
              } as any}
              classNames={{
                base: cn(" relative rounded-[12px] p-0",
                  "data-[hover=true]:bg-user-list-focus/40",
                  "data-[focus=true]:bg-transition",

                  "data-[selected=true]:!bg-user-list-focus",
                )
              }}
              key={item.id}
              textValue={item.name}
            >

              <ItemContent
                item={item}
                avatar={avatar}
                pinned={item.pinned}
                onPinned={onPinned && (() => onPinned(item.id))}
                onDelete={onDelete && (() => onDelete(item.id))}
                onSetName={onSetName && (() => onSetName(item.id))}
                onPress={(e) => {
                  onSelectionChange && onSelectionChange({ currentKey: item.id })
                }}
              />

            </ListboxItem>
          ))}
        </ScrollLoadList>
      </LockContext.Provider>
    </>


  )
}


const ItemContent = memo(({ item, avatar, onPinned, onSetName, onDelete, pinned, ...props }:
  { item, pinned, onPinned, onDelete, onSetName, avatar } & PanelProps) => {
  const [isOpen, setOpen] = useState(false)
  const { setLock, isLock } = useContext(LockContext)



  const device = useDevice()

  return (
    <>
      {/* 阻止在有弹窗时，触碰或者长按item时响应 */}
      {
        isLock && isOpen && createPortal(
          (<div
            className="bg-transparent fixed top-0 left-0 w-full h-full z-[51]"
            onPointerDown={() => {
              setLock(false)
              setOpen(false)
            }}
          >
          </div>),
          document.body
        )
      }

      <Panel
        data-show-bg={device === 'mobile' && isOpen}
        className={cn(
          "p-[8px] pr-[12px] select-none rounded-[12px]",
          "data-[show-bg=true]:bg-seleced-bg/40"
        )}
        onLongPress={() => {
          if (device === 'pc') return
          setOpen(true)
          setLock(true)
        }}
        {...props}
      >
        {pinned && <div className="absolute left-0 top-0 w-[12px] h-[12px] bg-chat-pin-triangle rounded-tl-[4px] triangle-clip"></div>}
        <div className="flex gap-2 items-center">
          {
            avatar?.(item) || <Avatar alt={item.name} className="flex-shrink-0" size="md" src={item.avatar} />
          }

          <div className="flex flex-col flex-1 overflow-hidden text-wrap">
            <span className="text-small break-all line-clamp-1">{item.name}</span>
            <span className="text-tiny text-default-400 break-all line-clamp-1">{item.scene}</span>
          </div>

          <MoreAction
            isOpen={isOpen}
            onOpenChange={(val) => {
              if (isLock) return
              setOpen(val)
            }}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            isHidden={device === 'mobile'}
            onPinned={onPinned}
            onDelete={onDelete}
            onSetName={onSetName}
            pinned={pinned}
            hiddenSetName={item.isGroup}
            hiddenDelete={item.isGroup}
          />

        </div>
      </Panel >
    </>

  )
})



const MoreAction = ({ isOpen,
  onOpenChange,
  onOpen,
  isHidden,
  onClose,
  onPinned,
  onDelete,
  onSetName,
  pinned,
  hiddenSetName,
  hiddenDelete
}) => {
  const t = useTranslations()

  return (
    <SelfDropdown
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <DropdownTrigger>
        <Button
          isIconOnly
          data-hidden={isHidden}
          className={cn(
            "flex items-center justify-center min-w-0 w-[24px] h-[24px] transition-all rounded-full bg-surface opacity-0",
            "group-data-[hover=true]:opacity-100",
            "data-[hidden=true]:w-0"
          )}
          onPress={onOpen}
        >
          <MoreHorizontal />
        </Button>
      </DropdownTrigger>

      <DropdownMenu aria-label="Static Actions">
        <DropdownItem
          key="pin"
          textValue="pin"
          className="py-[12px] rounded-xl data-[hover=true]:bg-seleced-bg"
          onPress={() => {
            onPinned && onPinned()
            onClose()
          }}
        >
          <div className="flex items-center gap-[8px] w-[140px]">
            {
              pinned ?
                <>
                  <UnPin />
                  <span>{t('chat.more.unPin')}</span>
                </>
                :
                <>
                  <Pin />
                  <span>{t('chat.more.pin')}</span>
                </>
            }
          </div>
        </DropdownItem>

        {hiddenSetName || <DropdownItem
          key="setName"
          textValue="Set nickname"
          className="py-[12px] rounded-xl data-[hover=true]:bg-seleced-bg"
          onPress={() => {
            onSetName && onSetName()
            onClose()
          }}
        >
          <div className="flex items-center gap-[8px] w-[140px]">
            <Edit className="[&_path]:stroke-theme-primary" />
            <span>{t('chat.more.setName')}</span>
          </div>
        </DropdownItem>}

        {
          !hiddenDelete ? <DropdownItem
            key="delete"
            className="py-[12px] rounded-xl data-[hover=true]:bg-seleced-bg"
            textValue="delete"
            onPress={() => {
              onDelete && onDelete()
              onClose()
            }}
          >
            <div className="flex items-center gap-[8px] w-[140px]">
              <Delete />
              <span>{t('chat.more.delete')}</span>
            </div>
          </DropdownItem> : null!
        }

      </DropdownMenu>

    </SelfDropdown>
  )
}